import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useUser } from "@clerk/clerk-react";
import { toast } from "sonner";
import { lessonService, type LessonWithProgress } from "@/lib/api/services/lesson.service";
import { lessonProgressService } from "@/lib/api/services/lesson-progress.service";
import type { Database } from "@/lib/supabase/supabase-types";

type LessonProgress = Database["public"]["Tables"]["lesson_progress"]["Row"];

type ProgressUpdateData = {
  seconds_watched: number;
  percent_watched: number;
  last_position_sec: number;
  furthest_watched_sec?: number;
  duration: number;
};

export function useLesson(lessonId: string) {
  const { user } = useUser();

  const lessonQuery = useQuery({
    queryKey: ["lesson", lessonId, user?.id],
    queryFn: async () => {
      if (!user?.id) throw new Error("No user ID");
      return await lessonService.getLesson(lessonId);
    },
    enabled: !!user?.id && !!lessonId,
    staleTime: 60 * 60 * 1000, // 1 hour
  });

  return {
    lesson: lessonQuery.data,
    isLoading: lessonQuery.isLoading,
    error: lessonQuery.error,
  };
}

export function useLessonProgress(lessonId: string) {
  const { user } = useUser();
  const queryClient = useQueryClient();

  const updateProgressMutation = useMutation({
    mutationFn: async (progressData: ProgressUpdateData) => {
      if (!user?.id) throw new Error("No user ID");

      // Convert percent from 0-1 to 0-100 for API, clamped to prevent exceeding 100
      return await lessonProgressService.upsertProgress(lessonId, {
        seconds_watched: progressData.seconds_watched,
        percent_watched: Math.min(progressData.percent_watched * 100, 100), // Convert to 0-100, clamped
        last_position_sec: progressData.last_position_sec,
        furthest_watched_sec: progressData.furthest_watched_sec || progressData.last_position_sec,
      });
    },
    onMutate: async (progressData) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: ["lesson", lessonId, user?.id] });

      // Snapshot the previous value
      const previousLesson = queryClient.getQueryData<LessonWithProgress>(["lesson", lessonId, user?.id]);

      // Optimistically update the lesson progress
      if (previousLesson) {
        const isCompleted = progressData.percent_watched >= 0.8;
        const now = new Date().toISOString();

        const optimisticProgress: LessonProgress = {
          id: previousLesson.progress?.id || `temp-${Date.now()}`,
          user_id: user?.id || '',
          lesson_id: lessonId,
          seconds_watched: Math.round(progressData.seconds_watched), // Round to integer
          percent_watched: Math.min(Math.round(progressData.percent_watched * 100), 100),
          last_position_sec: Math.round(progressData.last_position_sec), // Round to integer
          furthest_watched_sec: Math.round(progressData.furthest_watched_sec || progressData.last_position_sec), // Track furthest position
          last_updated_at: now,
          completed_at: isCompleted ? now : null,
          first_started_at: previousLesson.progress?.first_started_at || now,
          created_at: previousLesson.progress?.created_at || now,
        };

        queryClient.setQueryData<LessonWithProgress>(["lesson", lessonId, user?.id], {
          ...previousLesson,
          progress: optimisticProgress,
        });
      }

      return { previousLesson };
    },
    onError: (err, _progressData, context) => {
      // If the mutation fails, roll back
      if (context?.previousLesson) {
        queryClient.setQueryData(["lesson", lessonId, user?.id], context.previousLesson);
      }
      console.error('Progress update failed:', err);
      // Don't show error toast for progress updates as they happen frequently
    },
    onSuccess: (data) => {
      // Update with the actual server data
      const currentLesson = queryClient.getQueryData<LessonWithProgress>(["lesson", lessonId, user?.id]);
      if (currentLesson) {
        queryClient.setQueryData<LessonWithProgress>(["lesson", lessonId, user?.id], {
          ...currentLesson,
          progress: data,
        });
      }

      // Invalidate course-related queries to sync progress everywhere
      // This will update the course detail page with new lesson progress
      queryClient.invalidateQueries({
        queryKey: ["courses", "detail"],
        exact: false
      });

      // Also invalidate the courses list to update overall course progress
      queryClient.invalidateQueries({
        queryKey: ["courses"],
        exact: true
      });
    },
  });

  const markCompleteMutation = useMutation({
    mutationFn: async () => {
      if (!user?.id) throw new Error("No user ID");
      return await lessonProgressService.markComplete(lessonId);
    },
    onSuccess: (data) => {
      // Update lesson data
      const currentLesson = queryClient.getQueryData<LessonWithProgress>(["lesson", lessonId, user?.id]);
      if (currentLesson) {
        queryClient.setQueryData<LessonWithProgress>(["lesson", lessonId, user?.id], {
          ...currentLesson,
          progress: data,
        });
      }

      // Invalidate course-related queries to sync progress everywhere
      // This will update the course detail page with new lesson progress
      queryClient.invalidateQueries({
        queryKey: ["courses", "detail"],
        exact: false
      });

      // Also invalidate the courses list to update overall course progress
      queryClient.invalidateQueries({
        queryKey: ["courses"],
        exact: true
      });

      toast.success('Lesson completed!');
    },
    onError: (err) => {
      console.error('Failed to mark lesson complete:', err);
      toast.error('Failed to mark lesson as complete');
    },
  });

  return {
    updateProgress: updateProgressMutation.mutate,
    markComplete: markCompleteMutation.mutate,
    isUpdating: updateProgressMutation.isPending || markCompleteMutation.isPending,
  };
}

export function useCourseNavigation(courseId: string, currentLessonId: string) {
  const navigationQuery = useQuery({
    queryKey: ["course-navigation", courseId, currentLessonId],
    queryFn: async () => {
      const navigation = await lessonService.getCourseNavigation(courseId);
      
      const currentIndex = navigation.lessons.findIndex(lesson => lesson.id === currentLessonId);
      const previousLesson = currentIndex > 0 ? navigation.lessons[currentIndex - 1] : null;
      const nextLesson = currentIndex < navigation.lessons.length - 1 ? navigation.lessons[currentIndex + 1] : null;

      return {
        currentIndex,
        totalLessons: navigation.totalLessons,
        previousLesson,
        nextLesson,
        allLessons: navigation.lessons,
      };
    },
    enabled: !!courseId && !!currentLessonId,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  return {
    navigation: navigationQuery.data,
    isLoading: navigationQuery.isLoading,
    error: navigationQuery.error,
  };
}
